/**
 * useWidgetData — hook compartilhado pra widgets (R-IMPORT 3 / A3.3).
 *
 * Padroniza loading + error + retry + revalidate em todos os 19 widgets.
 * Cada widget passa um fetcher async + key (pra cache invalidation futuro).
 */
import { useCallback, useEffect, useRef, useState } from 'react';

export interface WidgetData<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

/**
 * @param fetcher função async sem args
 * @param refreshMs intervalo de auto-refresh (0 = nunca)
 */
export function useWidgetData<T>(
  fetcher: () => Promise<T>,
  refreshMs = 0,
): WidgetData<T> {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const mounted = useRef(true);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await fetcher();
      if (mounted.current) setData(r);
    } catch (e) {
      if (mounted.current) setError((e as Error).message);
    } finally {
      if (mounted.current) setLoading(false);
    }
  }, [fetcher]);

  useEffect(() => {
    mounted.current = true;
    void load();
    return () => {
      mounted.current = false;
    };
  }, [load]);

  useEffect(() => {
    if (!refreshMs || refreshMs <= 0) return;
    const id = window.setInterval(() => void load(), refreshMs);
    return () => window.clearInterval(id);
  }, [load, refreshMs]);

  return { data, loading, error, refetch: load };
}
